import { useEffect, useState } from 'react';
import type { AuthorityRow, AuthorityServiceRow } from '@shared/types/api';

interface ServiceModalProps {
  authorities: AuthorityRow[];
  editingSvc: AuthorityServiceRow | null;
  isOpen: boolean;
  isSaving: boolean;
  readOnly?: boolean;
  selectedAuthId: string;
  onClose: () => void;
  onSave: (data: { authorityId?: string; code: string; name: string }) => Promise<void>;
}

export function ServiceModal({
  authorities,
  editingSvc,
  isOpen,
  isSaving,
  readOnly = false,
  selectedAuthId,
  onClose,
  onSave,
}: ServiceModalProps) {
  const [authId, setAuthId] = useState(selectedAuthId);
  const [svcCode, setSvcCode] = useState(editingSvc ? editingSvc.code : '');
  const [svcName, setSvcName] = useState(editingSvc ? editingSvc.name : '');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setAuthId(selectedAuthId);
      setSvcCode(editingSvc ? editingSvc.code : '');
      setSvcName(editingSvc ? editingSvc.name : '');
      setErrorMsg(null);
    }
  }, [isOpen, selectedAuthId, editingSvc]);

  if (!isOpen) return null;

  const targetAuth = authorities.find((a) => a.id === (authId || selectedAuthId));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!svcCode.trim() || !svcName.trim()) return;

    setErrorMsg(null);
    try {
      await onSave({
        authorityId: editingSvc ? undefined : (authId || selectedAuthId),
        code: svcCode.trim(),
        name: svcName.trim(),
      });
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to save service';
      setErrorMsg(detail);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h3 className="text-base font-semibold text-gray-900">
            {readOnly ? `View Service: ${editingSvc?.name || ''}` : editingSvc ? 'Edit Service' : 'Add Service'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={readOnly ? (e) => { e.preventDefault(); onClose(); } : handleSubmit} className="flex flex-col">
          <fieldset disabled={readOnly} className="p-6 space-y-4">
            {errorMsg && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 font-medium flex items-center justify-between">
                <span>{errorMsg}</span>
                <button type="button" onClick={() => setErrorMsg(null)} className="text-red-500 hover:text-red-700 font-bold ml-2">×</button>
              </div>
            )}
            {!editingSvc && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Target Authority
                </label>
                <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm flex items-center justify-between">
                  <span>
                    {targetAuth ? targetAuth.name : 'Authority'}
                  </span>
                  {targetAuth?.code && (
                    <span className="rounded bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 border border-brand-200">
                      {targetAuth.code}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Service ID (Code) *
              </label>
              <input
                type="text"
                required
                value={svcCode}
                onChange={(e) => setSvcCode(e.target.value)}
                placeholder="e.g. MCA-001 or IT-001"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Service Name *
              </label>
              <input
                type="text"
                required
                value={svcName}
                onChange={(e) => setSvcName(e.target.value)}
                placeholder="e.g. Name Reservation"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </fieldset>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            {readOnly ? (
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                Close
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-800 disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save Service'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
